import React from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { Grid, Stack } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import PageIcon from '@atlaskit/icon/core/page';
import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import { token } from '@atlaskit/tokens';

import { CONTEXT_PATH } from '@confluence/named-routes';
import type { GetSpacePagesQuery_content_nodes } from '@confluence/onboarding-helpers/entry-points/hooks/useGetXFlowGettingStartedPage';

import { HTV2XflowLearningOpportunityCard } from './HTV2XflowLearningOpportunityCard';

export const JSW_PLANNING_TO_ACTION_LINK =
	'https://www.atlassian.com/blog/confluence/moving-from-idea-to-action-with-confluence-and-jira';
export const JSW_STREAMLINE_RELEASE_LINK =
	'https://www.atlassian.com/blog/confluence/streamline-release-notes-creation-with-confluence-and-jira';
export const JSM_FIVE_STEPS_LINK =
	'https://www.atlassian.com/itsm/knowledge-management/setting-up-a-knowledge-base';
export const JSM_LEARN_MORE_LINK = 'https://www.atlassian.com/customers/the-very-group';
export const JSM_ADD_ONS_LINK =
	'https://marketplace.atlassian.com/categories/it-support?product=confluence';

export const i18n = defineMessages({
	heading: {
		id: 'experiment-home-onboarding-takeover-v2.xflow.tailored-tips-heading',
		defaultMessage: 'Tailored tips for your team',
		description:
			'This will be shown as the section heading above a few cards that link to articles to help their team.',
	},
	jiraGetStartedTitle: {
		id: 'experiment-home-onboarding-takeover-v2.jira-get-started.title',
		defaultMessage: 'Get started with Confluence from Jira',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding. Clicking the card will redirect you to an atlassian article',
	},
	jiraPlanningToActionTitle: {
		id: 'experiment-home-onboarding-takeover-v2.jira-planning-to-action.title',
		defaultMessage: 'Go from planning to action with Confluence and Jira',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding. Clicking the card will redirect you to an atlassian article',
	},
	jiraStreamlineReleaseTitle: {
		id: 'experiment-home-onboarding-takeover-v2.jira-streamline-release.title',
		defaultMessage: 'Streamline release notes with Confluence and Jira',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding. Clicking the card will redirect you to an atlassian article',
	},
	jsmFiveStepsTitle: {
		id: 'experiment-home-onboarding-takeover-v2.jsm-five-steps.title',
		defaultMessage: '5 steps to set up a knowledge base for your service workflow',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding. Clicking the card will redirect you to an atlassian article',
	},
	jsmLearnMoreTitle: {
		id: 'experiment-home-onboarding-takeover-v2.jsm-learn-more.title',
		defaultMessage: 'Learn how The Very Group modernized their service management',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding. Clicking the card will redirect you to an atlassian article',
	},
	jsmAddOnsTitle: {
		id: 'experiment-home-onboarding-takeover-v2.jsm-add-ons.title',
		defaultMessage: 'Boost your service workflows with add-ons',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding. Clicking the card will redirect you to an atlassian article',
	},
	workLifeSubtitle: {
		id: 'experiment-home-onboarding-takeover-v2.work-life.subtitle',
		defaultMessage: 'Work Life article by Atlassian',
		description:
			'This will be shown as a subtitle on a card embedded into the home screen as a part of onboarding. It will describe what type of content the title refers to and where we will redirect.',
	},
	atlassianComSubtitle: {
		id: 'experiment-home-onboarding-takeover-v2.atlassian-com.subtitle',
		defaultMessage: 'Article on atlassian.com',
		description:
			'This will be shown as a subtitle on a card embedded into the home screen as a part of onboarding. It will describe what type of content the title refers to and where we will redirect.',
	},
	confluencePageSubtitle: {
		id: 'experiment-home-onboarding-takeover-v2.confluence-page.subtitle',
		defaultMessage: 'Confluence page',
		description:
			'This will be shown as a subtitle on a card embedded into the home screen as a part of onboarding. It will describe what type of content the title refers to and where we will redirect.',
	},
	atlassianMarketplaceSubtitle: {
		id: 'experiment-home-onboarding-takeover-v2.atlassian-marketplace.subtitle',
		defaultMessage: 'Atlassian Marketplace',
		description:
			'This will be shown as a subtitle on a card embedded into the home screen as a part of onboarding. It will describe what type of content the title refers to and where we will redirect.',
	},
});

export const JSWLearningCards = [
	{
		title: i18n.jiraGetStartedTitle,
		subtitle: i18n.confluencePageSubtitle,
		link: '#',
		icon: <PageIcon color={token('color.icon')} label="" />,
	},
	{
		title: i18n.jiraPlanningToActionTitle,
		subtitle: i18n.workLifeSubtitle,
		link: JSW_PLANNING_TO_ACTION_LINK,
		icon: <ArrowUpRightIcon color={token('color.icon')} label="" />,
	},
	{
		title: i18n.jiraStreamlineReleaseTitle,
		subtitle: i18n.workLifeSubtitle,
		link: JSW_STREAMLINE_RELEASE_LINK,
		icon: <ArrowUpRightIcon color={token('color.icon')} label="" />,
	},
];

export const JSMLearningCards = [
	{
		title: i18n.jsmFiveStepsTitle,
		subtitle: i18n.atlassianComSubtitle,
		link: JSM_FIVE_STEPS_LINK,
		icon: <ArrowUpRightIcon color={token('color.icon')} label="" />,
	},
	{
		title: i18n.jsmLearnMoreTitle,
		subtitle: i18n.atlassianComSubtitle,
		link: JSM_LEARN_MORE_LINK,
		icon: <ArrowUpRightIcon color={token('color.icon')} label="" />,
	},
	{
		title: i18n.jsmAddOnsTitle,
		subtitle: i18n.atlassianMarketplaceSubtitle,
		link: JSM_ADD_ONS_LINK,
		icon: <ArrowUpRightIcon color={token('color.icon')} label="" />,
	},
];

export const HTV2XflowLearningOpportunityCardsList = ({
	onboardingPeriodDay,
	hasJSWorJWM,
	hasJSM,
	xflowGettingStartedPage,
}: {
	onboardingPeriodDay: number;
	hasJSWorJWM: boolean;
	hasJSM: boolean;
	xflowGettingStartedPage: GetSpacePagesQuery_content_nodes | undefined;
}) => {
	const { formatMessage } = useIntl();

	if (!hasJSWorJWM && !hasJSM) return null;
	const cards = hasJSWorJWM ? JSWLearningCards : JSMLearningCards;

	return (
		<Stack space="space.150">
			<Heading size="xsmall">
				<FormattedMessage {...i18n.heading} />
			</Heading>
			<Grid gap="space.200" templateColumns="repeat(calc(3 - var(--homeIsSingleColumn, 0)), 1fr)">
				{cards.map((learningOpportunityCardContent) => {
					if (learningOpportunityCardContent.title === i18n.jiraGetStartedTitle) {
						if (Boolean(xflowGettingStartedPage)) {
							learningOpportunityCardContent.link = `${CONTEXT_PATH}${xflowGettingStartedPage?.links?.webui}`;
						} else {
							return null;
						}
					}

					return (
						<HTV2XflowLearningOpportunityCard
							key={`${formatMessage(learningOpportunityCardContent.title)}`}
							onboardingPeriodDay={onboardingPeriodDay}
							{...learningOpportunityCardContent}
						/>
					);
				})}
			</Grid>
		</Stack>
	);
};
